import { toast } from "sonner";
import { LogOut as ExitIcon } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { OPS, fmtDT, dayjs } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { CrudPage } from "@/components/CrudPage";
import { StatusBadge } from "@/components/Badges";

const H = { key: "hotel_id", label: "Struttura", type: "hotel", required: true };
const NOTE = { key: "note", label: "Note", type: "textarea" };
const dt = (key, label, required) => ({ key, label, type: "datetime", required });

const KINDS = {
  chiavi: {
    label: "Chiavi & Badge", close: "Restituita", closeState: "restituita",
    fields: [H, { key: "soggetto", label: "Chiave / Badge", required: true }, { key: "riferimento", label: "Consegnata a", required: true },
      dt("ingresso", "Ritiro", true), dt("uscita", "Restituzione"), { key: "stato", label: "Stato", type: "select", options: ["in uso", "restituita"], required: true }, NOTE],
    cols: [["soggetto", "Chiave / Badge"], ["riferimento", "Consegnata a"], ["ingresso", "Ritiro"], ["uscita", "Restituzione"], ["stato", "Stato"]],
    defaults: { stato: "in uso" },
  },
  fornitori: {
    label: "Fornitori", close: "Registra uscita",
    fields: [H, { key: "soggetto", label: "Ditta", required: true }, { key: "riferimento", label: "Referente / autista", required: true },
      { key: "dettaglio", label: "Motivo / materiale" }, dt("ingresso", "Ingresso", true), dt("uscita", "Uscita"), NOTE],
    cols: [["soggetto", "Ditta"], ["riferimento", "Referente"], ["dettaglio", "Motivo"], ["ingresso", "Ingresso"], ["uscita", "Uscita"]],
  },
  visitatori: {
    label: "Visitatori", close: "Registra uscita",
    fields: [H, { key: "soggetto", label: "Nominativo", required: true }, { key: "riferimento", label: "Documento", required: true },
      { key: "dettaglio", label: "Ospite di / motivo" }, dt("ingresso", "Ingresso", true), dt("uscita", "Uscita"), NOTE],
    cols: [["soggetto", "Nominativo"], ["riferimento", "Documento"], ["dettaglio", "Motivo"], ["ingresso", "Ingresso"], ["uscita", "Uscita"]],
  },
  smarriti: {
    label: "Oggetti smarriti", close: "Riconsegnato", closeState: "riconsegnato",
    fields: [H, { key: "soggetto", label: "Oggetto", required: true }, { key: "dettaglio", label: "Luogo ritrovamento", required: true },
      dt("ingresso", "Data ritrovamento", true), { key: "riferimento", label: "Riconsegnato a" }, dt("uscita", "Data riconsegna"),
      { key: "stato", label: "Stato", type: "select", options: ["in custodia", "riconsegnato", "smaltito"], required: true }, NOTE],
    cols: [["soggetto", "Oggetto"], ["dettaglio", "Luogo"], ["ingresso", "Ritrovato"], ["riferimento", "Riconsegnato a"], ["stato", "Stato"]],
    defaults: { stato: "in custodia" },
  },
};

const renderCol = ([key, label]) => ({
  key, label,
  render: (r) => (["ingresso", "uscita"].includes(key) ? <span className="font-mono text-xs">{r[key] ? fmtDT(r[key]) : "—"}</span>
    : key === "stato" ? <StatusBadge value={r.stato} /> : r[key] || "—"),
});

export default function Registri() {
  const { user } = useAuth();
  const close = async (kind, r, reload) => {
    const body = { uscita: dayjs().format("YYYY-MM-DDTHH:mm") };
    if (KINDS[kind].closeState) body.stato = KINDS[kind].closeState;
    try { await api.put(`/registers/${r.id}`, body); toast.success("Registrato"); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <PageHeader eyebrow="Registri informatizzati" title="Registri operativi" subtitle="Chiavi e badge, fornitori, visitatori e oggetti smarriti: tutto tracciato con data, ora e operatore." />
      <Tabs defaultValue="chiavi">
        <TabsList className="flex-wrap h-auto">
          {Object.entries(KINDS).map(([k, v]) => <TabsTrigger key={k} value={k} data-testid={`tab-register-${k}`}>{v.label}</TabsTrigger>)}
        </TabsList>
        {Object.entries(KINDS).map(([k, v]) => (
          <TabsContent key={k} value={k} className="mt-4">
            <CrudPage resource="registers" title={v.label} testid={`register-${k}`} fields={v.fields} columns={v.cols.map(renderCol)} writeRoles={OPS}
              params={{ kind: k }} newLabel="Nuova registrazione" defaults={() => ({ ingresso: dayjs().format("YYYY-MM-DDTHH:mm"), ...(v.defaults || {}) })}
              extraActions={(r, reload) => OPS.includes(user.role) && !r.uscita && (
                <Button size="sm" variant="outline" className="gap-1 mr-1" onClick={() => close(k, r, reload)} data-testid={`register-${k}-close-btn`}>
                  <ExitIcon className="w-3.5 h-3.5" />{v.close}
                </Button>
              )} />
          </TabsContent>
        ))}
      </Tabs>
    </>
  );
}
